import { ref, get } from "firebase/database";
import { db } from "../config/firebase_config";
import { Restaurant } from "../types/restaurant";

/**
 * Error class for restaurant service operations
 */
export class RestaurantServiceError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = "RestaurantServiceError";
  }
}

/**
 * Transform raw Firebase data to Restaurant interface
 */
function transformFirebaseData(id: string, data: any): Restaurant {
  return {
    dbID: id,
    name: data.name || "",
    about: data.about || "",
    address: data.address || "",
    addressOne: data.addressOne || "",
    addressTwo: data.addressTwo || "",
    area: data.area || "",
    city: data.city || "",
    state: data.state || "",
    country: data.country || "India",
    pincode: data.pincode || "",
    storeLat: parseFloat(data.storeLat) || 0,
    storeLong: parseFloat(data.storeLong) || 0,
    image: data.image || "",
    restRating: data.restRating?.toString() || "0",
    totalRatings: data.totalRatings?.toString(),
    restDiscount: data.restDiscount || "",
    status: data.status === "Active" ? "Active" : "DeActive",
    storeType: (["Hotel", "Restaurant", "PG", "Organization"].includes(
      data.storeType
    )
      ? data.storeType
      : "Restaurant") as "Hotel" | "Restaurant" | "PG" | "Organization",
    openingTime: data.openingTime || "",
    closingTime2: data.closingTime2 || "",
    timings: data.timings || "",
    isOnlineOrder: data.isOnlineOrder === "Yes" || data.isOnlineOrder === true,
    featured: data.featured === "Yes" || data.featured === true,
    pureVeg: data.pureVeg === "Yes" || data.pureVeg === true,
    isTableBooking: !!data.isTableBooking,
    floors: data.floors,
    isHighway: data.isHighway === true || String(data.isHighway).toLowerCase() === "true" || String(data.isHighway).toLowerCase() === "yes",
    isConvenienceFee: data.isConvenienceFee === true || String(data.isConvenienceFee).toLowerCase() === "true" || String(data.isConvenienceFee).toLowerCase() === "yes",
    isServiceFee: data.isServiceFee === true || String(data.isServiceFee).toLowerCase() === "true" || String(data.isServiceFee).toLowerCase() === "yes",
    restaurantNo: data.restaurantNo || "",
    restaurantOwnerNo: data.restaurantOwnerNo || "",
    isPrebooking: data.isPrebooking === true || String(data.isPrebooking).toLowerCase() === "true" || String(data.isPrebooking).toLowerCase() === "yes",
    amenities: data.amenities || [],
    biryani: data.biryani,
    breakFast: data.breakFast,
    chinese: data.chinese,
    desserts: data.desserts,
    healthy: data.healthy,
    indian: data.indian,
    pizza: data.pizza,
    shakes: data.shakes,
    shawarma: data.shawarma,
    snacks: data.snacks,
  };
}

/**
 * Fetch all restaurants from Firebase Realtime Database
 * @returns Promise<Restaurant[]> Array of restaurant objects
 * @throws RestaurantServiceError if fetch fails
 */
export async function fetchRestaurantsFromFirebase(): Promise<Restaurant[]> {
  try {
    const dbRef = ref(db, "/RestaurantMenu");
    const snapshot = await get(dbRef);

    if (!snapshot.exists()) {
      return [];
    }

    const data = snapshot.val();

    if (!data || typeof data !== "object") {
      throw new RestaurantServiceError(
        "Invalid data format received from Firebase",
        "INVALID_DATA_FORMAT"
      );
    }

    const restaurants: Restaurant[] = [];
    for (const [id, restaurantData] of Object.entries(data)) {
      try {
        restaurants.push(transformFirebaseData(id, restaurantData));
      } catch (error) {
        console.warn(
          `Failed to transform restaurant data for ID ${id}:`,
          error
        );
      }
    }

    return restaurants;
  } catch (error: any) {
    // Handle specific Firebase errors
    if (error instanceof RestaurantServiceError) {
      throw error;
    }

    if (error?.code === "PERMISSION_DENIED") {
      throw new RestaurantServiceError(
        "Permission denied: Unable to access restaurant data",
        "PERMISSION_DENIED",
        error
      );
    }

    if (
      error?.code === "NETWORK_ERROR" ||
      error?.message?.includes("network")
    ) {
      throw new RestaurantServiceError(
        "Network error: Unable to fetch restaurant data. Please check your connection.",
        "NETWORK_ERROR",
        error
      );
    }

    // Generic error fallback
    throw new RestaurantServiceError(
      "Failed to fetch restaurants from Firebase",
      "FETCH_ERROR",
      error
    );
  }
}

/**
 * Fetch a single restaurant by ID from Firebase
 * @param restaurantId - The restaurant ID to fetch
 * @returns Promise<Restaurant | null> Restaurant object or null if not found
 * @throws RestaurantServiceError if fetch fails
 */
export async function fetchRestaurantById(
  restaurantId: string
): Promise<Restaurant | null> {
  try {
    const dbRef = ref(db, `/RestaurantMenu/${restaurantId}`);
    const snapshot = await get(dbRef);

    if (!snapshot.exists()) {
      return null;
    }

    const data = snapshot.val();
    return transformFirebaseData(restaurantId, data);
  } catch (error: any) {
    if (error instanceof RestaurantServiceError) {
      throw error;
    }

    throw new RestaurantServiceError(
      `Failed to fetch restaurant with ID ${restaurantId}`,
      "FETCH_ERROR",
      error
    );
  }
}
